import { useEffect, useRef } from 'react';
import { useSimStore } from '../../store/useSimStore';
import { generateVentBuffer } from '../../engine/ventEngine';
import styles from './VentWaveform.module.css';

const SAMPLE_RATE = 250; // Hz
const BUFFER_DURATION = 12; // seconds of pre-computed samples

// GE Aisys CS2-style channel colours + baseline placement
const CHANNEL = {
  paw:    { color: '#F2E63D', baseline: 'bottom' }, // airway pressure — yellow
  flow:   { color: '#5DD55D', baseline: 'center' }, // flow — green (bipolar)
  volume: { color: '#F2E63D', baseline: 'bottom' }, // volume loop — yellow
  co2:    { color: '#FFFFFF', baseline: 'bottom' }, // capnograph — white
};

export default function VentWaveform({ channel, intubated = true }) {
  const canvasRef = useRef(null);
  const bufferRef = useRef(null);
  const writeHeadRef = useRef(0);
  const animationIdRef = useRef(null);
  const cssDimsRef = useRef({ width: 0, height: 0 });

  const ventSettings = useSimStore((s) => s.ventSettings);
  // EtCO₂ (shared monitor vital) drives the capnograph amplitude
  const etco2 = useSimStore((s) => s.vitals.EtCO2?.current ?? 38);

  // Regenerate the cycle buffer whenever the settings (or EtCO₂ for co2) change.
  // Not intubated → STANDBY: no positive-pressure ventilation, so draw a flat
  // baseline (a zeroed buffer) rather than synthesized breaths.
  useEffect(() => {
    if (!intubated) {
      bufferRef.current = new Float32Array(SAMPLE_RATE * BUFFER_DURATION);
      return;
    }
    const settings = channel === 'co2' ? { ...ventSettings, etco2 } : ventSettings;
    bufferRef.current = generateVentBuffer(channel, settings, SAMPLE_RATE, BUFFER_DURATION);
  }, [channel, ventSettings, etco2, intubated]);

  // DPR-aware sizing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const r = canvas.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      cssDimsRef.current = { width: r.width, height: r.height };
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  // Animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { color, baseline } = CHANNEL[channel];
    const samplesPerFrame = SAMPLE_RATE / 90; // sweep speed

    const draw = () => {
      const { width: cssW, height: cssH } = cssDimsRef.current;
      const buffer = bufferRef.current;
      if (cssW === 0 || cssH === 0 || !buffer) {
        animationIdRef.current = requestAnimationFrame(draw);
        return;
      }

      ctx.fillStyle = '#0b0f14';
      ctx.fillRect(0, 0, cssW, cssH);
      drawGrid(ctx, cssW, cssH, baseline);
      drawWaveform(ctx, cssW, cssH, buffer, writeHeadRef.current, color, baseline);

      writeHeadRef.current = (writeHeadRef.current + samplesPerFrame) % buffer.length;
      animationIdRef.current = requestAnimationFrame(draw);
    };

    animationIdRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animationIdRef.current);
  }, [channel]);

  return <canvas ref={canvasRef} className={styles.canvas} />;
}

function baselineY(cssH, baseline) {
  const margin = 6;
  return baseline === 'center' ? cssH / 2 : cssH - margin;
}

function sampleY(sample, cssH, baseline) {
  const margin = 6;
  if (baseline === 'center') {
    return cssH / 2 - sample * (cssH / 2 - margin) * 0.92;
  }
  // bottom baseline: sample 0 → bottom, sample 1 → top
  return (cssH - margin) - sample * (cssH - 2 * margin);
}

function drawGrid(ctx, cssW, cssH, baseline) {
  ctx.strokeStyle = 'rgba(90, 110, 130, 0.14)';
  ctx.lineWidth = 0.5;
  const spacing = 24;
  for (let x = 0; x < cssW; x += spacing) {
    ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, cssH); ctx.stroke();
  }
  for (let y = 0; y < cssH; y += spacing) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(cssW, y); ctx.stroke();
  }
  // Zero reference line
  ctx.strokeStyle = 'rgba(120, 140, 160, 0.35)';
  ctx.lineWidth = 1;
  const by = baselineY(cssH, baseline);
  ctx.beginPath(); ctx.moveTo(0, by); ctx.lineTo(cssW, by); ctx.stroke();
}

function drawWaveform(ctx, cssW, cssH, buffer, writeHead, color, baseline) {
  const N = buffer.length;
  const blankWidth = 18;
  const cursorX = (writeHead / N) * cssW;

  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.beginPath();
  let started = false;

  for (let k = 0; k < N; k++) {
    const x = (k / N) * cssW;
    const dist = (x - cursorX + cssW) % cssW;
    if (dist < blankWidth) { started = false; continue; }
    const y = sampleY(buffer[k], cssH, baseline);
    if (!started) { ctx.moveTo(x, y); started = true; } else { ctx.lineTo(x, y); }
  }
  ctx.stroke();

  // Eraser zone
  ctx.fillStyle = '#0b0f14';
  const eraseX = (cursorX + 1) % cssW;
  if (eraseX + blankWidth <= cssW) {
    ctx.fillRect(eraseX, 0, blankWidth, cssH);
  } else {
    ctx.fillRect(eraseX, 0, cssW - eraseX, cssH);
    ctx.fillRect(0, 0, blankWidth - (cssW - eraseX), cssH);
  }

  // Cursor tick
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(cursorX, 0); ctx.lineTo(cursorX, cssH); ctx.stroke();
}
